// usePerformanceMetrics.jsx
import { useEffect, useState, useCallback } from 'react';

const usePerformanceMetrics = () => {
  const [percentage, setPercentage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [totalData, setTotalData] = useState(0);
  const [uploadedData, setUploadedData] = useState(0);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [animatedPercentage, setAnimatedPercentage] = useState(0);

  const fetchData = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const totalRes = await fetch('/api/v1/get-total-products', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const uploadedRes = await fetch('/api/v1/get-total-data-upload-count', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!totalRes.ok) {
        throw new Error(`Total products API failed: ${totalRes.status}`);
      }
      if (!uploadedRes.ok) {
        throw new Error(`Uploaded data API failed: ${uploadedRes.status}`);
      }

      const totalData = await totalRes.json();
      const uploadedData = await uploadedRes.json();

      console.log('📦 Total Products:', totalData);
      console.log('📥 Uploaded Data:', uploadedData);

      const total = totalData.totalProducts || 0;
      const uploaded = uploadedData.totalDataUploadCount || 0;

      setTotalData(total);
      setUploadedData(uploaded);

      const percent = total > 0 ? ((uploaded / total) * 100) : 0;
      setPercentage(percent);
      setLastUpdated(new Date());

      let currentPercent = 0;
      const increment = percent / 50;
      const timer = setInterval(() => {
        currentPercent += increment;
        if (currentPercent >= percent) {
          setAnimatedPercentage(percent);
          clearInterval(timer);
        } else {
          setAnimatedPercentage(currentPercent);
        }
      }, 20);

    } catch (err) {
      console.error('❌ API Error:', err);
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    fetchData(true);
  };

  const getStatusColor = useCallback(() => {
    if (percentage >= 90) return '#4caf50'; // green
    if (percentage >= 70) return '#2196f3'; // blue
    if (percentage >= 50) return '#ff9800'; // orange
    return '#f44336'; // red
  }, [percentage]);

  const formatLastUpdated = useCallback(() => {
    if (!lastUpdated) return '';
    const now = new Date();
    const diff = Math.floor((now - lastUpdated) / 1000);

    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return `${Math.floor(diff / 3600)}h ago`;
  }, [lastUpdated]);

  return {
    percentage,
    loading,
    refreshing,
    totalData,
    uploadedData,
    error,
    lastUpdated,
    animatedPercentage,
    fetchData,
    handleRefresh,
    getStatusColor,
    formatLastUpdated,
  };
};

export default usePerformanceMetrics;